import { useState, useEffect, useMemo } from 'react';
import { API, graphqlOperation } from 'aws-amplify'; 
import { listAreas2, listStoreRecords } from 'graphql/queries';

import { HookStatus } from 'hooks/hookStatus'
import useCachedFetch from './useCachedFetch';

// Function to normalize store names
const normalizeStoreName = (name) => {
  return name.toLowerCase().replace(/[^a-z0-9\s]/g, '').split(/\s+/)
}

// Function to find the best match
const findBestMatch = (targetName, namesList) => {

  const targetWords = normalizeStoreName(targetName)
  let bestMatch = null
  let highestSimilarity = 0

  namesList.forEach(name => {
    const nameWords = normalizeStoreName(name)
    const matchingWords = targetWords.filter(word => nameWords.includes(word))
    const similarity = matchingWords.length / Math.max(targetWords.length, nameWords.length)

    if (similarity > highestSimilarity) {
      highestSimilarity = similarity
      bestMatch = name
    }
  })

  return bestMatch
}

/**
 * useAreas Hook
 * 
 * Provides area and store data with both filtered and unfiltered versions:
 * - data: Original data including ALL stores (even closed ones)
 * - filteredData: Data with closed stores excluded (based on StoreRecord.closeDate)
 * 
 * Most reports should use 'data' to maintain existing behavior.
 * Reports that need to exclude closed stores (like R0006) should use 'filteredData'.
 * 
 * R0002 includes closed stores at the bottom, so it uses 'data'.
 * R0006 excludes closed stores entirely, so it uses 'filteredData'.
 */
function useAreas() {
  
  const [status, setStatus] = useState(HookStatus.IDLE); 
  const [error, setError] = useState(null);
  // const [storeMap, setStoreMap] = useState(new Map())
  const [storeMaps, setStoreMaps] = useState({
    storeMap: new Map(),
    storeIdMap: new Map()
  })

  const storeRecords = useCachedFetch(fetchStoreRecords, setStatus)
  const data = useCachedFetch(fetchAreas, setStatus)

  // Temporarily keep original data reference for filtering
  const originalData = data;
  
  const companyAreas = useMemo(() => 
    originalData ? originalData.filter(area => area.type === 'Company').map(area => area.name) 
    : [], 
    [originalData]
  )
  
  const franchiseAreas = useMemo(() => 
    originalData ? originalData.filter(area => area.type === 'Franchise').map(area => area.name)
    : [],
    [originalData]
  )

  useEffect(() => {
    if(data && storeRecords){
      const storeRecordsMap = new Map()
      const storeRecordDetailsMap = new Map()
      // const storeNames = storeRecords.map(record => record.storeName)

      storeRecords.forEach(record => {
        storeRecordsMap.set(record.toastId, record.id)
        storeRecordDetailsMap.set(record.toastId, record)
        // storeRecordsMap.set(record.storeName, record.id)
      })

      const mapByLongName = new Map();
      const mapById = new Map();

      data.forEach(area => {
        area.stores.forEach(store => {
          let storeRecord = store.toastId ? storeRecordsMap.get(store.toastId) : null
          let storeRecordDetails = store.toastId ? storeRecordDetailsMap.get(store.toastId) : null
          
          // Note: We do NOT skip closed stores here because R0002 needs them in storeMap
          // The filtering happens later in the filteredData calculation
          
          // Check if store should be marked as closed based on closeDate
          let effectiveStatus = store.status;
          if (storeRecordDetails && storeRecordDetails.closeDate) {
            const closeDate = new Date(storeRecordDetails.closeDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            if (closeDate <= today) {
              effectiveStatus = 'Closed';
              console.log(`Store ${store.shortName} has closeDate ${storeRecordDetails.closeDate}, marking as Closed`);
            }
          }
          
          if (!store.toastId) {
            console.log(`No Toast ID found for store: ${store.shortName}`)
            effectiveStatus = 'Closed'
          } else if (!storeRecord) {
            console.log(`No matching store record found for store ${store.shortName}, toastId: ${store.toastId}`)
          }

          const toastGoLiveDate = toastGoLiveDates[store.shortName] || null
          const storeData = {
            shortName: store.shortName === 'Ankey' ? 'Ankeny' : store.shortName,
            area: area.areaName,
            areaType: area.areaType,
            status: effectiveStatus, // Use the effective status that considers closeDate
            type: store.type,
            id: store.storeId,
            storeRecordId: storeRecord ? storeRecord : null,
            toastId: store.toastId,
            city: storeLocations[store.shortName]?.city ?? null,
            state: storeLocations[store.shortName]?.state ?? null,
            zipcode: storeLocations[store.shortName]?.zipcode ?? null,
            toastGoLiveDate: toastGoLiveDate,
            closeDate: storeRecordDetails?.closeDate || null,
            thresholds: store.thresholds || {} // Add threshold assignments from grm.areas
          }

          mapByLongName.set(store.longName, storeData)

          if (storeData.storeRecordId) {
            mapById.set(storeData.storeRecordId, storeData)
          }
        })
      }) 

      setStoreMaps({ storeMap: mapByLongName, storeIdMap: mapById })     
    }
  }, [data, storeRecords])

  // Create filtered data that excludes closed stores
  const filteredData = useMemo(() => {
    if (!data || !storeRecords) return data;
    
    const storeRecordDetailsMap = new Map();
    storeRecords.forEach(record => {
      if (record.toastId) {
        storeRecordDetailsMap.set(record.toastId, record);
      }
    });
    
    return data.map(area => ({
      ...area,
      stores: area.stores.filter(store => {
        const storeRecordDetails = store.toastId ? storeRecordDetailsMap.get(store.toastId) : null;
        
        // Check if store is closed based on closeDate
        if (storeRecordDetails && storeRecordDetails.closeDate) {
          const closeDate = new Date(storeRecordDetails.closeDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          if (closeDate <= today) {
            return false; // Exclude closed store
          }
        }
        
        return true; // Include store
      })
    })).filter(area => area.stores.length > 0); // Only include areas that have at least one open store
  }, [data, storeRecords]);

  // Update company and franchise areas based on filtered data
  const filteredCompanyAreas = useMemo(() => 
    filteredData ? filteredData.filter(area => area.type === 'Company').map(area => area.name) 
    : [], 
    [filteredData]
  )
  
  const filteredFranchiseAreas = useMemo(() => 
    filteredData ? filteredData.filter(area => area.type === 'Franchise').map(area => area.name)
    : [],
    [filteredData]
  )

  return {
    data: originalData, // Keep original data with all stores (including closed)
    filteredData: filteredData, // New property with closed stores excluded
    storeMap: storeMaps.storeMap,
    storeIdMap: storeMaps.storeIdMap,
    companyAreas, // Original areas (including those with closed stores)
    franchiseAreas, // Original areas (including those with closed stores)
    filteredCompanyAreas, // New property with areas excluding closed stores
    filteredFranchiseAreas, // New property with areas excluding closed stores
    status,
    error 
  };
}

export default useAreas;

async function fetchAreas(setStatus) {

  setStatus(HookStatus.PENDING)

  try {
    const res = await API.graphql({ 
      query: listAreas2,
      variables: {
        report: 'store-admin',
        role: 'Admin',
        user: 'grmAdmin' 
      }
    });
    setStatus(HookStatus.SUCCESS)
    return res.data.listAreas2
  } catch (err) {
    console.log('Error fetching areas')
    setStatus(HookStatus.ERROR)
    throw err
  }  
  
}

async function fetchStoreRecords(setStatus) {
  setStatus(HookStatus.PENDING)

  // Fetch StoreRecords
  const variables = {
    limit: 1000
  }

  let allStoreRecords = []
  let nextToken = null

  do {
    if(nextToken){
      variables.nextToken = nextToken
    }

    try {
      const response = await API.graphql(graphqlOperation(listStoreRecords, variables))
      const { items, nextToken: newNextToken } = response.data.listStoreRecords
      allStoreRecords = allStoreRecords.concat(items)
      nextToken = newNextToken         
    } catch (err) {
      console.log('Error fetching StoreRecords', err)
      setStatus(HookStatus.ERROR)
      throw err
    }
  } while (nextToken)
   
  setStatus(HookStatus.SUCCESS)

  console.log('allStoreRecords', allStoreRecords)
  return allStoreRecords
}

const toastGoLiveDates = {
  'Ankeny': '2024-01-11',
  'Ankey': '2024-01-11',
  'Aspen Place': null,
  'Baybrook': '2024-08-12',
  'Bell Tower': '2024-08-26',
  'Boca': '2024-08-12',
  'Brookfield': '2024-09-09',
  'Brooklyn': '2024-09-23',
  'CITYCENTRE': '2024-07-22',
  'Casa Paloma': '2024-04-22',
  'Cinco Ranch': '2024-08-05',
  'DATG Palm Beach': '2024-08-19',
  'Destin': '2024-09-16',
  'Domain': '2024-07-08',
  'El Paso': '2024-08-26',
  'First Colony': '2024-07-29',
  'Fort Worth': '2024-08-05',
  'Fritz Farm': '2024-09-09',
  'Grand Boulevard': '2024-09-16',
  'Grapevine': '2024-07-22',
  'Green Valley': '2024-08-05',
  'Highland Village': '2024-06-24',
  'Hoover': '2024-08-28',
  'Huntsville': '2024-09-30',
  'Irving': '2024-06-17',
  'La Cantera': '2024-08-05',
  'La Palmera': '2024-07-22',
  'Legacy': '2024-08-19',
  'Madison': '2026-05-11',
  'Magnolia Park': null,
  'Meridian': '2024-11-04',
  'Mt. Pleasant': '2024-09-09',
  'Old Town': '2024-09-09',
  'Palazzo': '2024-08-19',
  'Park Lane': '2024-08-12',
  'Park West': '2024-07-08',
  'Prairiefire': '2024-08-26',
  'Quarry Village': '2024-07-29',
  'Realm': '2024-07-08',
  'SQ': '2024-08-26',
  'San Tan': '2024-06-10',
  'South Rainbow': '2024-07-29',
  'Sparks': '2024-09-09',
  'Stone Oak': '2024-07-15',
  'Towncenter': '2024-08-26',
  'WS West Shore': '2024-08-12',
  'WW DC Ranch': '2024-06-24',
  'Waterfront': '2024-09-16',
  'Watters Creek': '2024-07-15',
  'West Des Moines': '2024-06-03',
  'West Village': '2024-07-29',
}

const storeLocations = {
  'Ankeny': {
    city: 'Ankeny',
    state: 'IA',
    zipcode: '50023',
  },
  'Ankey': {
    city: 'Ankeny',
    state: 'IA',
    zipcode: '50023',
  },
  'Aspen Place': {
    city: 'Flagstaff',
    state: 'AZ',
    zipcode: '86001',
  },
  'Baybrook': {
    city: 'Friendswood',
    state: 'TX',
    zipcode: '77546',
  },
  'Bell Tower': {
    city: 'Fort Myers',
    state: 'FL',
    zipcode: '33907',
  },
  'Boca': {
    city: 'Las Vegas',
    state: 'NV',
    zipcode: '89145',
  },
  'Brookfield': {
    city: 'Brookfield',
    state: 'WI',
    zipcode: '53045',
  },
  'Brooklyn': {
    city: 'Brooklyn',
    state: 'NY',
    zipcode: '11201',
  },
  'CITYCENTRE': {
    city: 'Houston',
    state: 'TX',
    zipcode: '77024',
  },
  'Casa Paloma': {
    city: 'Chandler',
    state: 'AZ',
    zipcode: '85226',
  },
  'Cinco Ranch': {
    city: 'Katy',
    state: 'TX',
    zipcode: '77494',
  },
  'DATG Palm Beach': {
    city: 'PB Gardens',
    state: 'FL',
    zipcode: '33410',
  },
  'Destin': {
    city: 'Miramar Beach',
    state: 'FL',
    zipcode: '32550',
  },
  'Domain': {
    city: 'Austin',
    state: 'TX',
    zipcode: '78758',
  },
  'El Paso': {
    city: 'El Paso',
    state: 'TX',
    zipcode: '79925',
  },
  'First Colony': {
    city: 'Sugar Land',
    state: 'TX',
    zipcode: '77479',
  },
  'Fort Worth': {
    city: 'Fort Worth',
    state: 'TX',
    zipcode: '76109',
  },
  'Fritz Farm': {
    city: 'Lexington',
    state: 'KY',
    zipcode: '40517',
  },
  'Grapevine': {
    city: 'Grapevine',
    state: 'TX',
    zipcode: '76051',
  },
  'Green Valley': {
    city: 'Las Vegas',
    state: 'NV',
    zipcode: '89123',
  },
  'Highland Village': {
    city: 'Highland Village',
    state: 'TX',
    zipcode: '75077',
  },
  'Hoover': {
    city: 'Hoover',
    state: 'AL',
    zipcode: '35244',
  },
  'Huntsville': {
    city: 'Huntsville',
    state: 'AL',
    zipcode: '35802',
  },
  'Irving': {
    city: 'Irving',
    state: 'TX',
    zipcode: '75039',
  },
  'La Cantera': {
    city: 'San Antonio',
    state: 'TX',
    zipcode: '78256',
  },
  'La Palmera': {
    city: 'Corpus Christi',
    state: 'TX',
    zipcode: '78411',
  },
  'Legacy': {
    city: 'Plano',
    state: 'TX',
    zipcode: '75024',
  },
  'Madison': {
    city: 'Madison',
    state: 'AL',
    zipcode: '35757'
  },
  'Magnolia Park': {
    city: 'Greenville',
    state: 'SC',
    zipcode: '29607',
  },
  'Meridian': {
    city: 'Meridian',
    state: 'ID',
    zipcode: '83646',
  },
  'Mt. Pleasant': {
    city: 'Mount Pleasant',
    state: 'SC',
    zipcode: '29464',
  },
  'Old Town': {
    city: 'Scottsdale',
    state: 'AZ',
    zipcode: '85251',
  },
  'Palazzo': {
    city: 'Las Vegas',
    state: 'NV',
    zipcode: '89109',
  },
  'Park Lane': {
    city: 'Dallas',
    state: 'TX',
    zipcode: '75231',
  },
  'Park West': {
    city: 'Peoria',
    state: 'AZ',
    zipcode: '85345',
  },
  'Prairiefire': {
    city: 'Overland Park',
    state: 'KS',
    zipcode: '66223',
  },
  'Quarry Village': {
    city: 'San Antonio',
    state: 'TX',
    zipcode: '78209',
  },
  'Realm': {
    city: 'Lewisville',
    state: 'TX',
    zipcode: '75056',
  },
  'SQ': {
    city: 'Scotssdale',
    state: 'AZ',
    zipcode: '85254',
  },
  'San Tan': {
    city: 'Gilbert',
    state: 'AZ',
    zipcode: '85295',
  },
  'South Rainbow': {
    city: 'Las Vegas',
    state: 'NV',
    zipcode: '89118',
  },
  'Sparks': {
    city: 'Sparks',
    state: 'NV',
    zipcode: '89434',
  },
  'Stone Oak': {
    city: 'San Antonio',
    state: 'TX',
    zipcode: '78258',
  },
  'Towncenter': {
    city: 'Spring',
    state: 'TX',
    zipcode: '77380',
  },
  'West Des Moines': {
    city: 'Des Moines',
    state: 'IA',
    zipcode: '50266',
  },
  'WS West Shore': {
    city: 'Tampa',
    state: 'FL',
    zipcode: '33609',
  },
  'WW DC Ranch': {
    city: 'Scotsdale',
    state: 'AZ',
    zipcode: '85255',
  },
  'Waterfront': {
    city: 'Wichita',
    state: 'KS',
    zipcode: '67206',
  },
  'Watters Creek': {
    city: 'Allen',
    state: 'TX',
    zipcode: '75013',
  },
  'West Village': {
    city: 'Dallas',
    state: 'TX',
    zipcode: '75204',
  },
}